import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button, Typography, Box, Grid, MenuItem } from "@mui/material";
import {
  DataGrid,
  type GridPaginationModel,
} from "@mui/x-data-grid";
import type { RootState } from "../../../../..//store/Index";
import { useSelector } from "react-redux";
import CustomSelect from "../../../../../components/common/Select";
import apiService from "../../../../../service/Api";
import { toast } from "react-toastify";
import type { IComment } from "../../../../../types/comment";
import DownloadIcon from "@mui/icons-material/Download";
import { CommentsTableColumn } from "./CommentsTableColumn";
import { downloadApprovedComments } from "../utils/downloadApprovedComments";
import { handleBulkAction } from "../utils/handleBulkAction";
import SyncIcon from "@mui/icons-material/Sync";
import { SyncCommentsModal } from "./SyncCommentsModal";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { ImportCommentsModal } from "./ImportCommentsModal";
import PropTypes from "prop-types";

interface MemorialMessage {
  id: number;
  stream: number;
  sender_full_name: string | null;
  sender_email: string | null;
  is_approved: boolean;
  approved_by: number | null;
  parent: number | null;
  content: string;
  created_at: string;
  updated_at: string;
}

interface CommentsTableProps {
  commentsType: string; // example --> tribute comments, youtube comments
  endpoint: string;
  fetchApprovedComments: () => Promise<void>;
}

const CommentsTable: React.FC<CommentsTableProps> = ({
  commentsType,
  endpoint,
  fetchApprovedComments,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const bulkSelectAction = useRef<string>("approve");
  const { id } = useParams<{ id: string }>();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [importCommentsModalOpen, setImportCommentsModalOpen] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchedComments, setFetchedComments] = useState<IComment[]>([]);
  const [renderingComments, setRenderingComments] = useState<IComment[]>([]);
  const [checkedCommentsID, setCheckedCommentsID] = useState<string[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPaginationModel(newModel);
  };

  const fetchComments = useCallback(
    async (initialFetch: boolean) => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const params = {
          page: paginationModel.page + 1,
          page_size: paginationModel.pageSize,
          ordering: "-id",
        };
        const response = await apiService.get<{
          results: MemorialMessage[];
          count: number;
        }>(endpoint, params);

        const comments = response.data.results || [];

        // Map API comments to IComment interface
        const mappedComments: IComment[] = comments.map((comment) => ({
          id: comment.id.toString(),
          author: comment.sender_full_name || "Anonymous",
          authorEmail: comment.sender_email || "Anonymous",
          avatar: "/api/placeholder/32/32", // Placeholder as avatar is not in model
          content: comment.content,
          timestamp: new Date(comment.created_at).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
          source: comment.is_approved ? "Manual Approval" : undefined,
          isApproved: comment.is_approved,
        }));

        setRowCount(response.data.count);
        setFetchedComments(mappedComments);
        if (initialFetch) {
          setRenderingComments(mappedComments);
        } else {
          await fetchApprovedComments();
        }
      } catch (err: unknown) {
        console.error("Failed to fetch comments:", err);
        toast.error("Failed to load comments. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [endpoint, fetchApprovedComments, id, paginationModel.page, paginationModel.pageSize]
  );

  useEffect(() => {
    fetchComments(true);
  }, [fetchComments]);

  const toggleChecked = () => {
    if (checkIsChecked()) {
      setCheckedCommentsID([]);
      return;
    } else {
      const commentsID: string[] = renderingComments.map(
        (comment: IComment) => comment.id
      );
      setCheckedCommentsID([...commentsID]);
    }
  };

  const updateBulkAction = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & {
          target: {
            value: unknown;
            name: string;
          };
        })
  ) => {
    bulkSelectAction.current = String(e.target.value);
  };

  const checkIsChecked = () => {
    const renderingLength = renderingComments.length;
    const checkedCommentsLength = checkedCommentsID.length;

    if (renderingLength === 0 && checkedCommentsLength === 0) {
      return false;
    }

    if (renderingLength === checkedCommentsLength) {
      return true;
    }

    return false;
  };


  const refreshComments = (comment: IComment, isDeleting: boolean) => {
    if (isDeleting) {
      const newFetchedComments = fetchedComments.filter(
        (comm: IComment) => comm.id !== comment.id
      );
      const newRenderingComments = renderingComments.filter(
        (comm: IComment) => comm.id !== comment.id
      );
      setRenderingComments(newRenderingComments);
      setFetchedComments(newFetchedComments);

      return;
    }

    // update the arrays

    const newFetchedComments = fetchedComments.filter(
      (comm: IComment) => comm.id !== comment.id
    );
    const newRenderingComments = renderingComments.filter(
      (comm: IComment) => comm.id !== comment.id
    );
    setRenderingComments([comment, ...newRenderingComments]);
    setFetchedComments([comment, ...newFetchedComments]);
  };

  const filterComments = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & {
          target: {
            value: unknown;
            name: string;
          };
        })
  ) => {
    const value = e.target.value;

    switch (value) {
      case "all":
        setRenderingComments(fetchedComments);
        break;
      case "approved":
        setRenderingComments(fetchedComments.filter((c) => c.source));
        break;
      case "unapproved":
        setRenderingComments(fetchedComments.filter((c) => !c.source));
        break;
      default:
        setRenderingComments(fetchedComments);
    }
  };

  const checkIfCommentsApproved = () => {
    if (fetchedComments.length === 0) {
      return {
        bgColor: reduxTheme.primary_colors[300],
        readyToDownload: false,
      };
    }

    const approvedCommentAvailable = fetchedComments.some(
      (comment) => comment.isApproved === true
    );

    if (approvedCommentAvailable) {
      return {
        bgColor: reduxTheme.primary_colors[400],
        readyToDownload: true,
      };
    }
  };

  const syncComments = () => {
    setModalOpen(true);
  };

  return (
    <>
      <SyncCommentsModal open={modalOpen} setOpen={setModalOpen} />
      {importCommentsModalOpen && (
        <ImportCommentsModal
          setOpen={setImportCommentsModalOpen}
          fetchComments={fetchComments}
        />
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Grid container gap={3} alignItems={"center"} mt={2}>
          <Typography
            variant="h5"
            fontWeight={"bold"}
            sx={{ textTransform: "capitalize" }}
          >
            {commentsType}
          </Typography>

          <Box>
            <CustomSelect
              onChange={(e) => filterComments(e)}
              label={""}
              defaultValue={"all"}
              sx={{ width: "130px", borderRadius: "30px" }}
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </CustomSelect>
          </Box>

          <Box display={"flex"} gap={2} alignContent={"center"}>
            <CustomSelect
              onChange={(e) => updateBulkAction(e)}
              label={"Bulk Actions"}
              defaultValue={"approve"}
              sx={{ width: "150px" }}
              size="small"
            >
              <MenuItem value="approve">Approve</MenuItem>
              <MenuItem value="spam">Spam</MenuItem>
              <MenuItem value="unapprove">Pending</MenuItem>
            </CustomSelect>
            <Button
              variant="contained"
              onClick={() =>
                handleBulkAction(
                  bulkSelectAction,
                  checkedCommentsID,
                  renderingComments,
                  setRenderingComments,
                  fetchComments
                )
              }
            >
              apply
            </Button>
          </Box>
        </Grid>

        {commentsType === "tribute comments" && (
          <Box sx={{ gap: 2, display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              onClick={() => setImportCommentsModalOpen(true)}
            >
              <FileUploadIcon />
              Import
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: checkIfCommentsApproved()?.bgColor,
                mr: 2,
              }}
              disabled={!checkIfCommentsApproved()?.readyToDownload}
              onClick={() => downloadApprovedComments(id)}
            >
              <DownloadIcon />
              Approved
            </Button>
          </Box>
        )}

        {commentsType === "youtube comments" && (
          <Button
            variant="contained"
            sx={{
              mr: 2,
            }}
            onClick={syncComments}
          >
            <SyncIcon />
            Sync Comments
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 4 }}>
        {commentsType === "youtube comments" && (
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 2, ml: 1 }}>
            Last synced on 12/12/2025
          </Typography>
        )}
        <DataGrid
          rows={renderingComments}
          columns={CommentsTableColumn({
            toggleChecked,
            setCheckedCommentsID,
            checkedCommentsID,
            refreshComments,
            fetchApprovedComments,
          })}
          loading={isLoading}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          disableRowSelectionOnClick
          keepNonExistentRowsSelected
          pageSizeOptions={[10, 25, 50, 100]}
          getRowHeight={() => "auto"}
          getRowClassName={() => "data-grid-row"}
          sx={{
            zIndex: 1,
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "transparent", // or any color you want for no hover effect
            },

            "& .data-grid-row": {
              backgroundColor: "#fff",

              "& .MuiDataGrid-cell": {
                overflowWrap: "break-word",
                whiteSpace: "normal",
                lineHeight: "inherit",
              },
            },
            cursor: "pointer",
          }}
        />
      </Box>
    </>
  );
};

CommentsTable.propTypes = {
  commentsType: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  fetchApprovedComments: PropTypes.func.isRequired,
};

export default CommentsTable;
